import React from 'react';
import './style.scss';
import { Bar } from 'react-chartjs-2';
import {
  BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip,
} from 'chart.js';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { Spinner } from 'react-bootstrap';
import shortid from 'shortid';
import CustomTopContent from './CustomTopContent';

interface Props {
    title: string;
    topContent: React.ReactElement[];
    items: {label: string, bgColor: string, amount: number[] }[];
    currentView: number;
    setCurrentView: React.Dispatch<number>;
    labels: string[];
    isData: boolean;
    isLoading: boolean;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Default: React.FC<Props> = (props) => {
  const {
    topContent, items, title, currentView, setCurrentView, labels, isData, isLoading,
  } = props;

  return (
    <div className="chart-sales-main-container py-4 mb-4 mb-lg-0">
      <div className=" px-4 px-xl-5 mb-4 fz-16 fw-bold">{title}</div>
      {(() => {
        if (isLoading) {
          return (
            <div className="px-4 mb-4 px-xl-5 flexible-center h-100" style={{ minHeight: '150px' }}>
              <Spinner animation="border" />
            </div>
          );
        }
        if (isData) {
          return (
            <div>
              <div className="w-100">
                <div className="render-top-container px-4 px-xl-5">
                  {topContent.map((content, i) => (
                    <button
                      key={shortid.generate()}
                      type="button"
                      className={`fz-14 border-0 pb-3 ${currentView === i ? 'border-bottom border-3 border-color-primary fw-bold' : ''}`}
                      onClick={() => setCurrentView(i)}
                      style={{ minWidth: 150, backgroundColor: 'transparent' }}
                    >
                      {content}
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-100 bg-stroke mb-3" style={{ height: '1px' }} />
              <div className="h-100 mx-4 mx-xl-5">
                <div className="chart-sales-container">
                  <div className="h-100 w-100" style={{ minWidth: (labels.length * 100) }}>
                    <Bar
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom',
                            align: 'start',
                            labels: {
                              usePointStyle: true,
                            },
                          },
                        },
                        maintainAspectRatio: false,
                        datasets: {
                          bar: {
                            borderRadius: 20,
                            maxBarThickness: 10,
                          },
                        },
                        scales: {
                          yAxes: {
                            ticks: {
                              stepSize: 2000,
                              callback: ((tickValue) => (Number(tickValue) > 1000 ? `$${Number(tickValue) / 1000}K` : `$${Number(tickValue)}`)),
                              font: {
                                size: 14,
                              },
                              color: '#AEAEAE',
                            },
                          },
                          xAxes: {
                            ticks: {
                              font: {
                                size: 14,
                              },
                              color: '#AEAEAE',
                            },
                          },
                        },
                      }}
                      data={{
                        labels,
                        datasets: items.filter((x) => x.amount?.length).map((item) => ({
                          label: item.label,
                          data: labels.map((data, i) => item.amount[i]),
                          backgroundColor: item.bgColor,
                        })),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="px-4 mb-4 px-xl-5 flexible-center h-100" style={{ minHeight: '150px' }}>
            <div className="flexible-center flex-column">
              <div
                className="rounded-circle flexible-center mb-2"
                style={{ height: 65, width: 65, backgroundColor: '#F2F3F7' }}
              >
                <SignalCellularAltIcon sx={{ color: '#B1B6CE' }} />
              </div>
              <div className="fz-14">Not enough data to show.</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const Overview = {
  Default: React.memo(Default),
  CustomTopContent,
};

export default Overview;
